import {useMemo} from 'react';

/**
 * The most instances a set may create in one request. Decided by product rather
 * than by the walker: above this, creation is refused with a message naming the
 * real figure. Must stay in step with RecurrenceExpander.MAX_OCCURRENCES.
 */
export const MAX_OCCURRENCES = 500;

/**
 * How far the client keeps counting purely so a refusal can name a number.
 * The ceiling stops the set being CREATED; it should not stop us telling the user
 * they asked for 520. This is date arithmetic with no I/O, so probing further is
 * cheap, and the bound keeps a badly-configured daily-for-a-decade rule from
 * spinning.
 */
export const PROBE_LIMIT = 2000;

export interface TaskSetFormState {
	frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'YEARLY';
	intervalLength: number;
	/**
	 * WEEKLY only. Split from monthlyWeekday for the same reason RecurrenceRule
	 * splits them: a weekly rule falls on several days, a monthly nth-weekday rule
	 * falls on exactly one, and one field for both would make "the last Friday and
	 * Monday of the month" expressible.
	 */
	weekdays: string[];
	/** MONTHLY nth-weekday / last-weekday only. */
	monthlyWeekday: string | null;
	monthlyMode: 'DAY_OF_MONTH' | 'NTH_WEEKDAY' | 'LAST_WEEKDAY' | null;
	dayOfMonth: number | null;
	weekOrdinal: number | null;
	seedDate: string;
	boundType: 'COUNT' | 'DATE';
	boundCount: number | null;
	boundEndDate: string | null;
}

const DAY_ORDER = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/** Monday-based index, so it lines up with DAY_ORDER and with the backend. */
const mondayIndex = (d: Date): number => (d.getUTCDay() + 6) % 7;

const parseIso = (iso: string): Date => {
	const [year, month, day] = iso.split('-').map(Number);
	return new Date(Date.UTC(year, month - 1, day));
};

const toIso = (d: Date): string =>
	`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;

const lastDayOfMonth = (year: number, month: number): number => new Date(Date.UTC(year, month + 1, 0)).getUTCDate();

const nthWeekdayOfMonth = (year: number, month: number, weekday: string, ordinal: number): Date => {
	const target = DAY_ORDER.indexOf(weekday);
	const first = new Date(Date.UTC(year, month, 1));
	const offset = (target - mondayIndex(first) + 7) % 7;
	return new Date(Date.UTC(year, month, 1 + offset + (ordinal - 1) * 7));
};

const lastWeekdayOfMonth = (year: number, month: number, weekday: string): Date => {
	const target = DAY_ORDER.indexOf(weekday);
	const last = new Date(Date.UTC(year, month, lastDayOfMonth(year, month)));
	const back = (mondayIndex(last) - target + 7) % 7;
	return new Date(Date.UTC(year, month, last.getUTCDate() - back));
};

/** The first date on or after `from` whose weekday is selected. */
const nextOrSameSelectedWeekday = (from: Date, weekdays: string[]): Date => {
	for (let i = 0; i < 7; i++) {
		const candidate = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate() + i));
		if (weekdays.includes(DAY_ORDER[mondayIndex(candidate)])) {
			return candidate;
		}
	}
	return from;
};

/**
 * Mirrors RecurrenceExpander.weeklyOccurrence. The period-th occurrence counted
 * from `first`, not the next one after some cursor.
 *
 * Indexed because the caller asks for an arbitrary period: the slot within the
 * selected-day cycle gives the weekday, and how many times the cycle has wrapped
 * gives the weeks, times the interval. So the interval spaces the WEEKS, not the
 * occurrences -- "every two weeks on Mon and Wed" is Mon, Wed, skip a week, Mon,
 * Wed. Written iteratively it would return the same date for every period once
 * the answer fell inside first's own week.
 */
const weeklyOccurrence = (form: TaskSetFormState, first: Date, period: number): Date => {
	const picked = DAY_ORDER.map((_, i) => i).filter(i => form.weekdays.includes(DAY_ORDER[i]));
	if (picked.length === 0) {
		return first;
	}
	// nextOrSameSelectedWeekday put `first` on a selected day, so this is never -1.
	const firstIndex = picked.indexOf(mondayIndex(first));
	const slot = firstIndex + period;
	const weeksAhead = Math.floor(slot / picked.length) * form.intervalLength;
	const dayInWeek = picked[slot % picked.length];

	const weekStart = first.getUTCDate() - mondayIndex(first);
	return new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), weekStart + weeksAhead * 7 + dayInWeek));
};

/** The calendar month `step` months after `from`, as a year/month pair. */
const monthsFrom = (from: Date, step: number): {year: number; month: number} => {
	const monthIndex = from.getUTCMonth() + step;
	return {
		year: from.getUTCFullYear() + Math.floor(monthIndex / 12),
		month: ((monthIndex % 12) + 12) % 12,
	};
};

/**
 * Mirrors RecurrenceExpander.monthlyOccurrence: resolves the rule's day within a
 * given month. Only the month matters -- the day the caller arrived with is
 * discarded, which is why "the last Friday" lands correctly in every month
 * rather than drifting from the seed's own date.
 */
const monthlyOccurrenceIn = (form: TaskSetFormState, {year, month}: {year: number; month: number}): Date => {
	if (form.monthlyMode === 'NTH_WEEKDAY') {
		return nthWeekdayOfMonth(year, month, form.monthlyWeekday as string, form.weekOrdinal as number);
	}
	if (form.monthlyMode === 'LAST_WEEKDAY') {
		return lastWeekdayOfMonth(year, month, form.monthlyWeekday as string);
	}
	// Clamp rather than roll: "the 31st" in February means the 28th, not 3 March.
	return new Date(Date.UTC(year, month, Math.min(form.dayOfMonth as number, lastDayOfMonth(year, month))));
};

const occurrenceForPeriod = (form: TaskSetFormState, seed: Date, period: number): Date => {
	const step = form.intervalLength * period;

	if (form.frequency === 'DAILY') {
		return new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth(), seed.getUTCDate() + step));
	}
	if (form.frequency === 'WEEKLY') {
		return weeklyOccurrence(form, seed, period);
	}
	if (form.frequency === 'YEARLY') {
		// Clamp rather than roll, matching Java's plusYears: 29 February in a
		// non-leap year is 28 February. Date.UTC would roll it to 1 March and the
		// preview would name dates the backend never creates.
		const yearlyYear = seed.getUTCFullYear() + step;
		const yearlyMonth = seed.getUTCMonth();
		return new Date(
			Date.UTC(yearlyYear, yearlyMonth, Math.min(seed.getUTCDate(), lastDayOfMonth(yearlyYear, yearlyMonth)))
		);
	}

	return monthlyOccurrenceIn(form, monthsFrom(seed, step));
};

/**
 * Mirrors RecurrenceExpander.firstOccurrence: the earliest date satisfying the
 * rule that is not before the seed.
 *
 * The seed is where the series starts looking, not necessarily its first
 * occurrence. Both the weekly and the monthly rule snap FORWARD, so a set never
 * starts earlier than the date the user picked -- and instance one always obeys
 * the pattern the config view describes.
 */
const firstOccurrence = (form: TaskSetFormState, seed: Date): Date => {
	if (form.frequency === 'WEEKLY') {
		return form.weekdays.length > 0 ? nextOrSameSelectedWeekday(seed, form.weekdays) : seed;
	}
	// A null mode is a half-built form; there is no rule to satisfy yet.
	if (form.frequency === 'MONTHLY' && form.monthlyMode) {
		const candidate = monthlyOccurrenceIn(form, monthsFrom(seed, 0));
		return candidate.getTime() < seed.getTime() ? monthlyOccurrenceIn(form, monthsFrom(seed, 1)) : candidate;
	}
	return seed;
};

/**
 * Mirrors RecurrenceExpander on the backend. Kept in step with it by sharing the
 * same test cases -- if one changes, both change.
 */
export const expandRecurrence = (form: TaskSetFormState): string[] => {
	if (!form.seedDate) {
		return [];
	}
	if (form.frequency === 'WEEKLY' && form.weekdays.length === 0) {
		return [];
	}
	if (!form.intervalLength || form.intervalLength < 1) {
		// An interval below one never advances, so the walk would run to the probe
		// limit. Validation refuses this too; returning nothing keeps the preview
		// from spinning while the user is mid-edit.
		return [];
	}

	// Snapped to the first date the rule actually allows, so period 0 obeys the
	// pattern rather than being the raw seed.
	const first = firstOccurrence(form, parseIso(form.seedDate));
	const end = form.boundType === 'DATE' && form.boundEndDate ? parseIso(form.boundEndDate) : null;

	const dates: string[] = [];
	let current = first;
	let period = 0;

	// Counts past the ceiling on purpose, up to PROBE_LIMIT, so an over-ceiling
	// rule can be reported as "520 tasks" rather than "more than 500".
	while (dates.length < PROBE_LIMIT) {
		if (form.boundType === 'COUNT' && dates.length >= (form.boundCount || 0)) {
			break;
		}
		if (end && current.getTime() > end.getTime()) {
			break;
		}

		dates.push(toIso(current));
		period += 1;
		current = occurrenceForPeriod(form, first, period);
	}

	return dates;
};

export interface TaskSetPreview {
	dates: string[];
	/** What the rule asks for, which may be above the ceiling. */
	count: number;
	overCeiling: boolean;
	/** True when the count is itself a floor rather than exact -- see PROBE_LIMIT. */
	countIsAtProbeLimit: boolean;
}

export const useTaskSetPreview = (form: TaskSetFormState): TaskSetPreview =>
	useMemo(() => {
		const dates = expandRecurrence(form);
		const overCeiling = dates.length > MAX_OCCURRENCES;
		return {
			// Never show occurrences that would not be created as if they were.
			dates: overCeiling ? dates.slice(0, MAX_OCCURRENCES) : dates,
			count: dates.length,
			overCeiling,
			countIsAtProbeLimit: dates.length >= PROBE_LIMIT,
		};
	}, [form]);
